import React, { useEffect, useMemo } from 'react'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import IPModal from '../../../../../components/IPModal'
import { useCustomReducer } from '../../../../../base/customHooks'
import Swal from 'sweetalert2'
import { getCompanyApi } from '../../../components/api'
import { ICompany } from '../../../components/models/models'
import Table from '../../../../../components/Table'
import IPButton from '../../../../../components/IPButton'
import IPSelectBox from '../../../../../components/IPSelectBox'
import { ISelectOption } from '../../../../../components/IPSelectBox/IPSelectBox'
import IPCheckbox from '../../../../../components/IPCheckBox'
import { ListPosCompanyApi } from '../service/listPosCompanyApi'
import { UpdatePosCompanyApi } from '../service/updatePosCompanyApi'
import { ListPosCompanyHistoryApi } from '../service/listPosCompanyHistoryApi'
import { ListPosDataApi } from '../service/listPosDataApi'

import {
  IPosCompany,
  IPosCompanyHistory,
  IPosCompanyRequest,
  IPosData,
} from '../models/models'

interface IPosCompanyState {
  showDetail: boolean

  selectedCompanyName: string
  selectedPosName: string

  posCompanyList: IPosCompany[]
  posCompanyHistoryList: IPosCompanyHistory[]

  companies: ICompany[]
  posList: IPosData[]

  selectedCompanyId: number
  selectedPOSId: number
  selectedEnabled: boolean
}

type CompanyLookupModel = ICompany & {
  Id?: number
  Code?: number | string
  SapCode?: number | string
  Name?: string
}

function PosCompany(props: BaseComponentWithContextProps) {
  const getInitialState = (): IPosCompanyState => {
    return {
      showDetail: false,

      selectedCompanyName: '',
      selectedPosName: '',

      posCompanyList: [],
      posCompanyHistoryList: [],

      companies: [],
      posList: [],

      selectedCompanyId: 0,
      selectedPOSId: 0,
      selectedEnabled: true,
    }
  }

  const initialState = getInitialState()
  const [state, setState] = useCustomReducer<IPosCompanyState>(initialState)

  useEffect(() => {
    loadPageData()
  }, [])

  const loadPageData = async () => {
    props.showLoading()

    try {
      await Promise.all([
        listPosCompany(false),
        getCompanies(false),
        listPosData(false),
      ])
    } finally {
      props.hideLoading()
    }
  }

  const getCompanyName = (item: IPosCompany): string => {
    const companies = state.companies as CompanyLookupModel[]

    const company = companies.find(x =>
      x.Id === item.CompanyId ||
      x.Code?.toString() === item.CompanyCode?.toString() ||
      x.SapCode?.toString() === item.CompanyCode?.toString()
    )

    return company?.Name || item.CompanyCode || item.CompanyId.toString()
  }

  const getSelectedCompanyName = (): string => {
    const companies = state.companies as CompanyLookupModel[]
    const company = companies.find(x => x.Id === state.selectedCompanyId)

    return company?.Name || state.selectedCompanyId.toString()
  }

  const getSelectedPosName = (): string => {
    const pos = state.posList.find(x => x.Id === state.selectedPOSId)

    return pos?.Name || state.selectedPOSId.toString()
  }

  const listPosCompany = async (showLoading: boolean = true) => {
    if (showLoading) {
      props.showLoading()
    }

    try {
      const api = new ListPosCompanyApi({})
      const response = await api.listPosCompany()

      setState({
        posCompanyList: response,
      })
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: err?.description || 'POS şirket listesi alınırken hata oluştu.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    } finally {
      if (showLoading) {
        props.hideLoading()
      }
    }
  }

  const getCompanies = async (showLoading: boolean = true) => {
    if (showLoading) {
      props.showLoading()
    }

    try {
      const response: ICompany[] = await getCompanyApi()

      setState({
        companies: response,
      })
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: err?.description || 'Şirket listesi alınırken hata oluştu.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    } finally {
      if (showLoading) {
        props.hideLoading()
      }
    }
  }

  const listPosData = async (showLoading: boolean = true) => {
    if (showLoading) {
      props.showLoading()
    }

    try {
      const api = new ListPosDataApi({})
      const response = await api.listPosData()

      setState({
        posList: response,
      })
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: err?.description || 'POS listesi alınırken hata oluştu.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    } finally {
      if (showLoading) {
        props.hideLoading()
      }
    }
  }

  const savePosCompany = async () => {
    if (state.selectedCompanyId <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Lütfen şirket seçiniz.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })

      return
    }

    if (state.selectedPOSId <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Lütfen POS seçiniz.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })

      return
    }

    const request: IPosCompanyRequest = {
      CompanyId: state.selectedCompanyId,
      POSId: state.selectedPOSId,
      Enabled: state.selectedEnabled,
    }

    props.showLoading()

    try {
      const response = await new UpdatePosCompanyApi({}).updatePosCompany(request)

      if (response) {
        Swal.fire({
          icon: 'success',
          title: `${getSelectedCompanyName()} şirketi için ${getSelectedPosName()} ${state.selectedEnabled ? 'açılmıştır.' : 'kapatılmıştır.'}`,
          showConfirmButton: true,
          allowOutsideClick: false,
        })

        setState({
          selectedCompanyId: 0,
          selectedPOSId: 0,
          selectedEnabled: true,
        })

        await listPosCompany(false)
      }
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: err?.description || 'Kayıt işleminde hata oluştu.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    } finally {
      props.hideLoading()
    }
  }

  const updatePosCompany = async (data: IPosCompany) => {
    const nextEnabledValue = !data.Enabled

    const request: IPosCompanyRequest = {
      CompanyId: data.CompanyId,
      POSId: data.POSId,
      Enabled: nextEnabledValue,
    }

    props.showLoading()

    try {
      const response = await new UpdatePosCompanyApi({}).updatePosCompany(request)

      if (response) {
        const updatedList = state.posCompanyList.map(item => {
          if (item.CompanyId === data.CompanyId && item.POSId === data.POSId) {
            return {
              ...item,
              Enabled: nextEnabledValue,
            }
          }

          return item
        })

        setState({
          posCompanyList: updatedList,
        })

        Swal.fire({
          icon: 'success',
          title: `${getCompanyName(data)} şirketi için ${data.POSName} ${nextEnabledValue ? 'açılmıştır.' : 'kapatılmıştır.'}`,
          showConfirmButton: true,
          allowOutsideClick: false,
        })
      }
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: err?.description || 'Güncelleme işleminde hata oluştu.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    } finally {
      props.hideLoading()
    }
  }

  const listPosCompanyHistory = async (data: IPosCompany) => {
    props.showLoading()

    try {
      const api = new ListPosCompanyHistoryApi({})
      const response = await api.listPosCompanyHistory(data.CompanyId, data.POSId)

      if (!response || response.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Bu şirket ve POS için geçmiş bilgisi yoktur.',
          showConfirmButton: true,
          allowOutsideClick: false,
        })

        return
      }

      setState({
        showDetail: true,
        selectedCompanyName: getCompanyName(data),
        selectedPosName: data.POSName,
        posCompanyHistoryList: response,
      })
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: err?.description || 'POS şirket geçmişi alınırken hata oluştu.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    } finally {
      props.hideLoading()
    }
  }

  const tableColumns = useMemo(
    () => [
      {
        Header: '#',
        accessor: 'col1',
      },
      {
        Header: 'Aç-Kapa',
        accessor: 'col2',
      },
      {
        Header: 'Şirket',
        accessor: 'col3',
      },
      {
        Header: 'POS',
        accessor: 'col4',
      },
      {
        Header: 'Tarihçe',
        accessor: 'col5',
      },
    ],
    []
  )
  const companyOptions = useMemo(
    () =>
      (state.companies as CompanyLookupModel[]).map(company => ({
        label: company.Name || '',
        value: company.Id || 0,
      })),
    [state.companies]
  )

  const posOptions = useMemo(
    () =>
      state.posList.map(pos => ({
        label: pos.Name,
        value: pos.Id,
      })),
    [state.posList]
  )

  const selectedCompanyOption = useMemo(
    () => companyOptions.find(option => Number(option.value) === state.selectedCompanyId) || null,
    [companyOptions, state.selectedCompanyId]
  )

  const selectedPosOption = useMemo(
    () => posOptions.find(option => Number(option.value) === state.selectedPOSId) || null,
    [posOptions, state.selectedPOSId]
  )

  const tableData = useMemo(
    () =>
      state.posCompanyList.map((item, index) => {
        const companyName = getCompanyName(item)

        return {
          col1: index + 1,
          col2: (
            <IPCheckbox
              id={`pos-company-active-${item.CompanyId}-${item.POSId}`}
              checked={item.Enabled}
              onCheckedChange={() => updatePosCompany(item)}
              removeMarginBottom
            />
          ),
          col3: companyName,
          col4: item.POSName,
          col5: (
            <IPButton
              text='Tarihçe'
              type='button'
              onClick={() => listPosCompanyHistory(item)}
              style={{
                padding: '0px',
                width: '120px',
                height: '30px',
                color: 'white',
                borderColor: 'red',
              }}
              className='btn btn-danger'
            />
          ),
        }
      }),
    [state.posCompanyList, state.companies]
  )

  const groupedTableData = useMemo(() => {
    const groupMap: Record<number, { companyName: string, items: IPosCompany[] }> = {}

    state.posCompanyList.forEach(item => {
      if (!groupMap[item.CompanyId]) {
        groupMap[item.CompanyId] = {
          companyName: getCompanyName(item),
          items: [],
        }
      }

      groupMap[item.CompanyId].items.push(item)
    })

    return Object.keys(groupMap)
      .map(Number)
      .sort((a, b) => a - b)
      .map(companyId => ({
        companyName: groupMap[companyId].companyName,
        rows: groupMap[companyId].items.map((item, index) => ({
          col1: index + 1,
          col2: (
            <IPCheckbox
              id={`pos-company-active-${item.CompanyId}-${item.POSId}`}
              checked={item.Enabled}
              onCheckedChange={() => updatePosCompany(item)}
              removeMarginBottom
            />
          ),
          col3: groupMap[companyId].companyName,
          col4: item.POSName,
          col5: (
            <IPButton
              text='Tarihçe'
              type='button'
              onClick={() => listPosCompanyHistory(item)}
              style={{
                padding: '0px',
                width: '120px',
                height: '30px',
                color: 'white',
                borderColor: 'red',
              }}
              className='btn btn-danger'
            />
          ),
        })),
      }))
  }, [state.posCompanyList, state.companies])

  return (
    <div className='container'>
      <div className='content col-lg-12 m-auto'>
        <div className='card mb-3'>
          <div className='card-header'>
            <span className='h4'>POS Şirket Ekle</span>
          </div>

          <div className='card-body'>
            <div className='row'>
              <div className='col-md-4'>
                <label>Şirket</label>
                <IPSelectBox
                  name='selectedCompanyId'
                  placeholder='Şirket seçiniz'
                  isClearable
                  options={companyOptions}
                  value={selectedCompanyOption as ISelectOption}
                  onChangeSingle={option =>
                    setState({
                      selectedCompanyId: Number(option?.value || 0),
                    })
                  }
                />

              </div>
              <div className='col-md-4'>
                <label>POS</label>
                <IPSelectBox
                  name='selectedPOSId'
                  placeholder='POS seçiniz'
                  isClearable
                  options={posOptions}
                  value={selectedPosOption as ISelectOption}
                  onChangeSingle={option =>
                    setState({
                      selectedPOSId: Number(option?.value || 0),
                    })
                  }
                />

              </div>
              <div className='col-md-2'>
                <label>Durum</label>

                <div style={{ marginTop: '8px' }}>
                  <IPCheckbox
                    id='new-pos-company-enabled'
                    checked={state.selectedEnabled}
                    onCheckedChange={checked =>
                      setState({
                        selectedEnabled: checked,
                      })
                    }
                    removeMarginBottom
                  />

                  <span style={{ marginLeft: '8px' }}>
                    {state.selectedEnabled ? 'Açık' : 'Kapalı'}
                  </span>
                </div>
              </div>

              <div className='col-md-2 d-flex align-items-end'>
                <IPButton
                  text='Ekle'
                  type='button'
                  onClick={savePosCompany}
                  className='btn btn-success'
                  style={{
                    width: '100%',
                    height: '36px',
                    color: 'white',
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className='card'>
          <div className='card-header'>
            <span className='h4'>POS Şirket Açma | Kapama</span>
          </div>

          <div className='card-body'>
            {
              groupedTableData.length > 0
                ? groupedTableData.map(group => (
                  <div key={group.companyName} className='mb-4'>
                    <h5 className='mb-2'>{group.companyName}</h5>
                    <Table columns={tableColumns} data={group.rows} currentPage={0} />
                  </div>
                ))
                : <Table columns={tableColumns} data={tableData} currentPage={0} />
            }
          </div>
        </div>
      </div>

      <IPModal
        show={state.showDetail}
        onClose={() =>
          setState({
            showDetail: false,
            selectedCompanyName: '',
            selectedPosName: '',
            posCompanyHistoryList: [],
          })
        }
        title={`${state.selectedCompanyName} - ${state.selectedPosName} Açma | Kapama Logları`}
        width={900}
        hideConfirm={true}
      >
        <table
          id='datatable'
          className='table table-bordered table-hover dataTable'
          role='grid'
          aria-describedby='datatable_info'
        >
          <thead className='tableodd'>
            <tr role='row'>
              <th>#</th>
              <th>İşlem Tarihi</th>
              <th>Şirket</th>
              <th>POS</th>
              <th>İşlemi Yapan Kullanıcı</th>
              <th>İşlem</th>
            </tr>
          </thead>

          <tbody>
            {
              state.posCompanyHistoryList &&
              state.posCompanyHistoryList.length > 0 &&
              state.posCompanyHistoryList
                .sort((a, b) => new Date(b.CreatedDate).getTime() - new Date(a.CreatedDate).getTime())
                .map((history, index) => (
                  <tr role='row' className='odd' key={history.Id}>
                    <td>{index + 1}</td>
                    <td>{new Date(history.CreatedDate).toLocaleString('tr-TR')}</td>
                    <td>{history.CompanyCode}</td>
                    <td>{history.POSName}</td>
                    <td>{history.UserName}</td>
                    <td>{history.Enabled ? 'Açıldı' : 'Kapatıldı'}</td>
                  </tr>
                ))
            }
          </tbody>
        </table>
      </IPModal>
    </div>
  )
}

export default componentWithContext(PosCompany)

